import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {testIdProps} from '@github-ui/test-id-props'
import {Button, Stack, useConfirm} from '@primer/react'
import {useCallback} from 'react'

import {ViewOptionsMenuUI} from '../../api/stats/contracts'
import {ViewerPrivileges} from '../../helpers/viewer-privileges'
import {isViewConfigDirty} from '../../hooks/use-view-state-reducer/get-view-dirty-states'
import type {BaseViewState, ViewIsDirtyStates} from '../../hooks/use-view-state-reducer/types'
import {useViews} from '../../hooks/use-views'
import {Resources} from '../../strings'
import {SaveViewSplitButton} from '../common/save-view-split-button'
import styles from './view-changes-button.module.css'

export function ViewChangeButtons({
  view,
  setOpen,
}: {
  view: BaseViewState & ViewIsDirtyStates
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
}) {
  const {hasWritePermissions} = ViewerPrivileges()
  const {saveCurrentViewState, resetViewState, duplicateCurrentViewState} = useViews()
  const displayButtonEnabled = useFeatureFlag('memex_display_button_config_menu')
  const confirm = useConfirm()

  const configDirty = isViewConfigDirty(view)
  const filterDirty = view.isFilterDirty

  const handleDuplicateView = useCallback(async () => {
    await duplicateCurrentViewState(view.number, undefined, {
      ui: ViewOptionsMenuUI,
    })
  }, [duplicateCurrentViewState, view.number])

  const handleSaveButtonClick = useCallback(
    async (event?: React.SyntheticEvent) => {
      event?.stopPropagation()

      if (displayButtonEnabled) {
        const shouldSave = await confirm({
          title: Resources.getConfirmationTitle({filterDirty, configDirty, viewName: view.name}),
          content: Resources.getConfirmationDescription({filterDirty, configDirty, includeDisplayFallback: true}),
          confirmButtonContent: Resources.saveChanges,
          confirmButtonType: 'primary',
          width: 'large',
        })

        if (!shouldSave) {
          return
        }
      }

      // When both filter and config are dirty, save everything at once
      // When only config is dirty, save just the config
      // When disabled, save everything (original behavior)
      const saveScope = displayButtonEnabled ? (filterDirty && configDirty ? undefined : 'config') : undefined
      await saveCurrentViewState(
        view.number,
        {
          ui: ViewOptionsMenuUI,
        },
        saveScope,
      )
      setOpen(false)
    },
    [confirm, configDirty, displayButtonEnabled, filterDirty, saveCurrentViewState, setOpen, view.name, view.number],
  )

  const handleResetChanges = useCallback(async () => {
    // When display button is enabled, only reset the config
    // When disabled, reset everything (original behavior)
    const resetScope = displayButtonEnabled ? 'config' : undefined
    resetViewState(
      view.number,
      {
        ui: ViewOptionsMenuUI,
      },
      resetScope,
    )

    setOpen(false)
  }, [displayButtonEnabled, resetViewState, setOpen, view.number])

  const isConfigDirty = displayButtonEnabled ? configDirty : view.isViewStateDirty

  if (!isConfigDirty) {
    return null
  }

  return (
    <Stack direction="horizontal" justify="end" gap="condensed" padding="normal" className={styles.Footer}>
      <Button onClick={handleResetChanges} {...testIdProps('view-options-menu-reset-changes-button')}>
        {Resources.discardChanges}
      </Button>
      {hasWritePermissions && !view.isDeleted ? (
        displayButtonEnabled ? (
          <SaveViewSplitButton
            onSave={handleSaveButtonClick}
            onDuplicate={handleDuplicateView}
            saveButtonText={displayButtonEnabled ? Resources.setDefaultChanges : Resources.saveChanges}
            disabled={!isConfigDirty}
            testIdPrefix="view-options-menu"
          />
        ) : (
          <Button
            variant="primary"
            onClick={handleSaveButtonClick}
            disabled={!isConfigDirty}
            {...testIdProps('view-options-menu-save-changes-button')}
          >
            {Resources.saveChanges}
          </Button>
        )
      ) : null}
    </Stack>
  )
}
